/**
 * Waveforms.
 *
 * Peaks are computed once, on the device that made the recording, and travel
 * with the clip. The alternative — decoding on every viewer's machine — means
 * downloading the whole file before anything can be drawn, so a voice note
 * would show an empty bar until it had finished loading.
 */

export const PEAK_BUCKETS = 56;

const AudioCtx = () => window.AudioContext || window.webkitAudioContext;

/**
 * One shared context for decoding. Browsers cap how many AudioContexts a page
 * may hold — around six in Chrome — so a feed of voice notes that each opened
 * their own would simply stop decoding partway down.
 */
let decodeCtx = null;
const decodeContext = () => {
  if (!decodeCtx || decodeCtx.state === 'closed') decodeCtx = new (AudioCtx())();
  return decodeCtx;
};

/**
 * Reduce a clip to a small array of 0..1 amplitudes, one per bar.
 *
 * Peak rather than average: a mean over thousands of samples flattens speech
 * into a featureless line, and the point of the drawing is to show where the
 * talking is.
 */
export async function computePeaks(blob, buckets = PEAK_BUCKETS) {
  try {
    const decoded = await decodeContext().decodeAudioData(await blob.arrayBuffer());
    const channel = decoded.getChannelData(0);
    const per = Math.floor(channel.length / buckets) || 1;

    const peaks = [];
    for (let i = 0; i < buckets; i += 1) {
      let peak = 0;
      const start = i * per;
      for (let j = start; j < start + per && j < channel.length; j += 1) {
        const value = Math.abs(channel[j]);
        if (value > peak) peak = value;
      }
      peaks.push(peak);
    }

    // Normalise against the loudest bar so a quiet recording still reads as
    // speech rather than a flat line.
    const loudest = Math.max(...peaks, 0.01);
    return peaks.map((p) => Math.min(1, p / loudest));
  } catch {
    return [];   // an undecodable clip is still a perfectly good clip
  }
}

/** Peaks for a clip that arrived without any — older uploads, mainly. */
export async function peaksFromUrl(url, buckets = PEAK_BUCKETS) {
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    return await computePeaks(await res.blob(), buckets);
  } catch {
    return [];
  }
}

/**
 * A live level meter over the microphone, for the bar that moves while you
 * are recording. Returns a read() giving the current 0..1 loudness.
 */
export function createLevelMeter(stream) {
  // Its own context: this one is closed when the recording ends, and closing
  // the shared decode context would break every player on the page.
  const ctx = new (AudioCtx())();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.6;
  source.connect(analyser);

  const buffer = new Uint8Array(analyser.fftSize);

  return {
    read() {
      analyser.getByteTimeDomainData(buffer);
      // Root mean square around the 128 midpoint, scaled to something that
      // looks like speech rather than hugging the floor.
      let sum = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const v = (buffer[i] - 128) / 128;
        sum += v * v;
      }
      return Math.min(1, Math.sqrt(sum / buffer.length) * 3.2);
    },
    stop() {
      try { source.disconnect(); } catch { /* already torn down */ }
      ctx.close?.();
    },
  };
}

export const clockLabel = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};
